import { TableDefinition, BfxColDef } from "./TableDefinition";
import { ALGO_MONITOR_TABLE_DEFINITION_VER } from "./TableDefinitionFactory.constants";

import { BlotterType, AssetClass } from "shared/cubiq-model-multi-asset";
import { RateCache } from "../rate/impl/RateCache";

import { BasePricingService } from "../lookup/pricing/impl/BasePricingService";

import { CcyLookupService } from "../lookup/model/impl/CcyLookupService";
import { CcyPairLookupService } from "../lookup/model/impl/CcyPairLookupService";
import { CurrencyPairCalendarLookupService } from "../lookup/model/impl/CurrencyPairCalendarLookupService";
import { ClientLookupService } from "../lookup/model/impl/ClientLookupService";
import { AccountLookupService } from "../lookup/model/impl/AccountLookupService";
import { TraderGroupLookupService } from "../lookup/model/impl/TraderGroupLookupService";
import { RateSourceLookupService } from "../lookup/model/impl/RateSourceLookupService";
import { SalesGroupLookupService } from "../lookup/model/impl/SalesGroupLookupService";
import { UserLookupService } from "../lookup/model/impl/UserLookupService";
import { SystemConfigPromiseService } from "shared/auth-services";
import { ProfilePromiseActionService } from "../profile/impl/ProfilePromiseActionService";

import { Injectable, Optional } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";

import { OrdersTableDefinitionFactory } from "./OrdersTableDefinitionFactory";
import {
  COLUMN_ACCOUNT_CODE,
  COLUMN_ACCOUNT_NAME,
  COLUMN_CLIENT_BRANCH,
  COLUMN_CLIENT_CODE,
  COLUMN_CLIENT_NAME,
  COLUMN_CLIENT_ORDER_ID,
  COLUMN_DEALT_CURRENCY,
  COLUMN_DEALT_DIR,
  COLUMN_DEALT_PERCENTAGE,
  COLUMN_DESK_ID,
  COLUMN_ENTRY_USER_ID,
  COLUMN_FWD_ROLL,
  COLUMN_INSTRUMENT,
  COLUMN_MARKUP,
  COLUMN_ORDER_ACTIVATION_DATE,
  COLUMN_ORDER_AMOUNT,
  COLUMN_ORDER_CLIENT_COMMENTS,
  COLUMN_ORDER_DEALT_FWD_PTS,
  COLUMN_ORDER_DEALT_TRACKING,
  COLUMN_ORDER_ENTRY_DATE,
  COLUMN_ORDER_EXECUTED_AMOUNT,
  COLUMN_ORDER_EXECUTED_AMOUNT_USD,
  COLUMN_ORDER_EXECUTED_PERC,
  COLUMN_ORDER_EXECUTION_CHANNEL,
  COLUMN_ORDER_EXPIRY_DATE,
  COLUMN_ORDER_FIXING_DATE,
  COLUMN_ORDER_FIXING_SOURCE_CODE,
  COLUMN_ORDER_FWD_DEALT_TRACKING,
  COLUMN_ORDER_ID,
  COLUMN_ORDER_MKT_VWAP_ALL_IN,
  COLUMN_ORDER_MKT_VWAP_PTS,
  COLUMN_ORDER_MKT_VWAP_SPOT,
  COLUMN_ORDER_MONITORING_SIDE,
  COLUMN_ORDER_PASS_THROUGH_PARAMETERS,
  COLUMN_ORDER_PRIVATE_COMMENT,
  COLUMN_ORDER_PRODUCT,
  COLUMN_ORDER_REQUEST_RATE,
  COLUMN_ORDER_STATE,
  COLUMN_ORDER_STRATEGY_PARAMETERS,
  COLUMN_ORDER_SUSPENSION_FROM_DATE,
  COLUMN_ORDER_SUSPENSION_TO_DATE,
  COLUMN_ORDER_TRAILING_TRIGGER,
  COLUMN_ORDER_TYPE,
  COLUMN_ORDER_USD_AMOUNT,
  COLUMN_ORDER_VWAP_ALL_IN,
  COLUMN_ORDER_VWAP_PTS,
  COLUMN_ORDER_VWAP_SPOT,
  COLUMN_RATE_SOURCE_CODE,
  COLUMN_REMAINING_AMOUNT,
  COLUMN_REMAINING_AMOUNT_USD,
  COLUMN_SALES_GROUP_ID,
  COLUMN_SPOT_MARKET_MID,
  COLUMN_STP_AGG,
  COLUMN_STP_HOLD,
  COLUMN_STRATEGY_ID,
  COLUMN_TARGET_DESK_ID,
  COLUMN_TRADER_GROUP_NAMES,
  COLUMN_VALUE_DATE,
  COLUMN_VOL_PER_MIN,
} from "./OrdersTableDefinitionFactory.constants";
import { DealtPricingService } from "shared/cubiq-services/lookup/pricing/impl/DealtPricingService";

import { ClientModelPrivatization } from "./privatization/ClientModelPrivatization";
import { AccountModelPrivatization } from "./privatization/AccountModelPrivatization";
import { ClientTraderModelPrivatization } from "./privatization/ClientTraderModelPrivatization";
import { BfxSysConfigUtilService } from "shared/auth-services";
import { BfxConfigService } from "shared/auth-services/system-config/bfx-config.service";
import { CustomFieldLookupService } from "../lookup/model/impl/CustomFieldLookupService";
import { FixingSourceLookupService } from "../lookup/model/impl/FixingSourceLookupService";
import { AlgoStrategyLookupService } from "../lookup/model/impl/AlgoStrategyLookupService";
import { AtdlTooltipUiRep } from "./adtl/atdl-tooltip-ui-rep";

import * as CubiqTS from "shared/cubiq-model-multi-asset";

import { LoggerService } from "@bfx/logger";

const logger = LoggerService.for(__dirname);

const EXCLUDED_FIELDS: string[] = [
  COLUMN_DEALT_PERCENTAGE,
  "isAutoEligible",
  COLUMN_ORDER_FWD_DEALT_TRACKING,
  COLUMN_ORDER_DEALT_TRACKING,
  COLUMN_ORDER_SUSPENSION_FROM_DATE,
  COLUMN_ORDER_SUSPENSION_TO_DATE,
  COLUMN_ORDER_TRAILING_TRIGGER,
  "trailingPips",
  COLUMN_ORDER_EXECUTION_CHANNEL,
  COLUMN_ORDER_DEALT_FWD_PTS,
  COLUMN_SPOT_MARKET_MID,
  COLUMN_RATE_SOURCE_CODE,
  COLUMN_ORDER_MONITORING_SIDE,
];

const CUSTOM_PLACEHOLDER: string = "CUSTOM_PLACEHOLDER";

const ALGO_MONITOR_COL_ORDER: string[] = [
  COLUMN_ORDER_STATE, // Order State
  COLUMN_CLIENT_NAME, // Client Name
  COLUMN_CLIENT_CODE, // Client ID
  COLUMN_ACCOUNT_NAME, // Account Name
  COLUMN_ACCOUNT_CODE, // Account ID
  COLUMN_INSTRUMENT, // Instrument
  COLUMN_DEALT_DIR, // Dir
  COLUMN_DEALT_CURRENCY, // Ccy
  COLUMN_ORDER_AMOUNT, // Order Amt
  COLUMN_ORDER_USD_AMOUNT, // Order Amt USD
  COLUMN_ORDER_TYPE, // Order Type
  COLUMN_ORDER_PRODUCT, // Product
  COLUMN_VALUE_DATE, // Value Date
  COLUMN_REMAINING_AMOUNT, // Open Amt
  COLUMN_REMAINING_AMOUNT_USD, // Open Amt USD
  COLUMN_ORDER_EXECUTED_AMOUNT, // Fill Amt
  COLUMN_ORDER_EXECUTED_PERC, // Fill %
  COLUMN_ORDER_EXECUTED_AMOUNT_USD, // Fill Amt USD
  COLUMN_ORDER_MKT_VWAP_SPOT, // Mkt Spot
  COLUMN_ORDER_MKT_VWAP_PTS, // Mkt Pts
  COLUMN_ORDER_MKT_VWAP_ALL_IN, // Mkt All-In
  COLUMN_ORDER_VWAP_SPOT, // Client Spot
  COLUMN_ORDER_VWAP_PTS, // Client Pts
  COLUMN_ORDER_VWAP_ALL_IN, // Client All-In
  COLUMN_MARKUP, // Markup
  COLUMN_DESK_ID, // Trader Group
  COLUMN_ORDER_ACTIVATION_DATE, // Good From
  COLUMN_ORDER_EXPIRY_DATE, // Good Until
  COLUMN_ORDER_CLIENT_COMMENTS, // Client Comments
  COLUMN_ORDER_PRIVATE_COMMENT, // Private Comments
  COLUMN_ENTRY_USER_ID, // Entry User
  COLUMN_ORDER_ENTRY_DATE, // Entry Date
  COLUMN_ORDER_ID, // Order ID
  COLUMN_STRATEGY_ID, // Strategy ID
  COLUMN_CLIENT_ORDER_ID, // ClOrderId
  COLUMN_SALES_GROUP_ID, // Sales Group
  COLUMN_CLIENT_BRANCH, // Branch
  COLUMN_ORDER_PASS_THROUGH_PARAMETERS, // Pass-Through Parameters
  COLUMN_TARGET_DESK_ID, // Target Group
  COLUMN_ORDER_FIXING_SOURCE_CODE, // Fixing Code
  COLUMN_ORDER_FIXING_DATE, // Fixing Date
  COLUMN_STP_HOLD, // Hold STP
  CUSTOM_PLACEHOLDER, // Custom Fields
  COLUMN_STP_AGG, // STP Aggregation
  COLUMN_FWD_ROLL, // Forward Roll
  COLUMN_VOL_PER_MIN, // Vol/Min
  COLUMN_ORDER_STRATEGY_PARAMETERS, // Strategy Params
  COLUMN_ORDER_REQUEST_RATE,
];

const ALGO_MONITOR_DEF_COLS: string[] = [
  COLUMN_ORDER_STATE, // Order State
  COLUMN_CLIENT_NAME, // Client Name
  COLUMN_INSTRUMENT, // Instrument
  COLUMN_DEALT_DIR, // Dir
  COLUMN_DEALT_CURRENCY, // Ccy
  COLUMN_ORDER_AMOUNT, // Order Amt
  COLUMN_ORDER_TYPE, // Order Type
  COLUMN_ORDER_PRODUCT, // Product
  COLUMN_VALUE_DATE, // Value Date
  COLUMN_REMAINING_AMOUNT, // Open Amt
  COLUMN_ORDER_EXECUTED_AMOUNT, // Fill Amt
  COLUMN_ORDER_EXECUTED_PERC, // Fill %
  COLUMN_ORDER_MKT_VWAP_SPOT, // Mkt Spot
  COLUMN_ORDER_MKT_VWAP_PTS, // Mkt Pts
  COLUMN_ORDER_MKT_VWAP_ALL_IN, // Mkt All-In
  COLUMN_ORDER_VWAP_SPOT, // Client Spot
  COLUMN_ORDER_VWAP_PTS, // Client Pts
  COLUMN_ORDER_VWAP_ALL_IN, // Client All-In
  COLUMN_MARKUP, // Markup
  COLUMN_ORDER_ACTIVATION_DATE, // Good From
  COLUMN_ORDER_EXPIRY_DATE, // Good Until
  COLUMN_ORDER_ID, // Order ID
  COLUMN_VOL_PER_MIN, // Vol/Min
  COLUMN_ORDER_STRATEGY_PARAMETERS, // Strategy Params
];

@Injectable()
export class AlgoMonitorDefinitionFactory extends OrdersTableDefinitionFactory {
  constructor(
    profileService: ProfilePromiseActionService,
    rateCache: RateCache,
    @Optional() rateSourceLookup: RateSourceLookupService,
    @Optional() myClientLookup: ClientLookupService,
    @Optional() myAccountLookup: AccountLookupService,
    @Optional() mySalesGroupLookup: SalesGroupLookupService,
    @Optional() myTraderGroupLookup: TraderGroupLookupService,
    @Optional() ccyLookupService: CcyLookupService,
    @Optional() ccyPairLookupService: CcyPairLookupService,
    @Optional() userLookupService: UserLookupService,
    @Optional() sysConfigService: SystemConfigPromiseService,
    @Optional() translateService: TranslateService,
    @Optional() myBasePricingService: BasePricingService,
    @Optional() myDealtPricingService: DealtPricingService,
    @Optional() fixingSourceLookupService: FixingSourceLookupService,
    @Optional() algoStrategyLookupService: AlgoStrategyLookupService,
    @Optional()
    currencyPairCalendarLookupService: CurrencyPairCalendarLookupService,
    @Optional() cmp: ClientModelPrivatization,
    @Optional() amp: AccountModelPrivatization,
    @Optional() ctmp: ClientTraderModelPrivatization,
    @Optional() atdlTooltipUiRep: AtdlTooltipUiRep,
    @Optional() sysConfigUtil: BfxSysConfigUtilService,
    @Optional() configService: BfxConfigService,
    @Optional() customFieldLookupService: CustomFieldLookupService,
  ) {
    super(
      profileService,
      rateCache,
      rateSourceLookup,
      myClientLookup,
      myAccountLookup,
      mySalesGroupLookup,
      myTraderGroupLookup,
      ccyLookupService,
      ccyPairLookupService,
      userLookupService,
      sysConfigService,
      translateService,
      myBasePricingService,
      myDealtPricingService,
      fixingSourceLookupService,
      algoStrategyLookupService,
      currencyPairCalendarLookupService,
      cmp,
      amp,
      ctmp,
      atdlTooltipUiRep,
      sysConfigUtil,
      configService,
      customFieldLookupService,
    );
  }

  public createDef(): TableDefinition {
    let tableDefinition = super.createDef();

    tableDefinition.algoTableDefinition = true;

    EXCLUDED_FIELDS.forEach((c) => tableDefinition.removeColumnDefinition(c));

    this.addAlgoSpecificColumns(tableDefinition);

    this.reOrderColumns(tableDefinition);
    let requestRateCol = tableDefinition.columns.find((c) => c.field === COLUMN_ORDER_REQUEST_RATE);
    if (requestRateCol) {
      // Rename Request Rate to Limit Price for Algo Monitor
      requestRateCol.headerName = "Limit Price";
    }

    // Rename avg price to Client All-In
    let vwapCol = tableDefinition.columns.find((c) => c.field === COLUMN_ORDER_VWAP_ALL_IN);
    if (vwapCol) {
      vwapCol.headerName = "Client All-In (Avg)";
      vwapCol.minWidth = this.threeWs;
    }

    return tableDefinition;
  }

  reOrderColumns(tableDefinition: TableDefinition) {
    let currCol: number = 0;

    ALGO_MONITOR_COL_ORDER.forEach((col) => {
      if (CUSTOM_PLACEHOLDER === col) {
        this.customFieldLookupService.getAllCustomFields().forEach((item) => {
          if (item.isEnabledFg) {
            let done: boolean = this.setColPosition(tableDefinition, item.fieldId, currCol);
            if (done) {
              currCol = currCol + 1;
            }
          }
        });
      } else {
        let done: boolean = this.setColPosition(tableDefinition, col, currCol);
        if (done) {
          currCol = currCol + 1;
        }
      }
    });
  }

  setColPosition(tableDefinition: TableDefinition, col: string, currCol: number) {
    let ret: boolean = false;
    let colIndex = tableDefinition.columnIndexMap.get(col);
    if (null != colIndex) {
      let colDef: BfxColDef = tableDefinition.columns.at(colIndex);
      const alreadyHasConfig: boolean = this.restoreFromConfig(colDef);
      const visibleByDefault: boolean = ALGO_MONITOR_DEF_COLS.includes(col);

      if (!alreadyHasConfig && null != colDef) {
        // Add a displacement so any restored cols will always come before new cols
        colDef.position = currCol + 1000;
        colDef.hide = !visibleByDefault;
        ret = true;
      }
    }
    return ret;
  }

  protected getMarketBar(td: TableDefinition): any {
    return null;
  }

  private hideFromGrid(column: BfxColDef): void {
    column.omitFromPicker = true;
    column.hide = true;
  }

  protected getBlotterTypeStr(): string {
    let type: string = super.getBlotterTypeStr();
    return type + "_ALGOMON";
  }

  public getBlotterType(): BlotterType {
    return BlotterType.ORDER;
  }

  public getAssetClass(): AssetClass {
    return AssetClass.FIX;
  }

  public getDefaultDealtMode(): any {
    return true;
  }

  public getVersionIdentifier(): string {
    return ALGO_MONITOR_TABLE_DEFINITION_VER;
  }
}
